import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { Router } from "@angular/router";
import { DeleteConfirmationComponent } from "../../forms/delete-confirmation/delete-confirmation.component";
import { BookingComponent } from "../../modals/booking/booking.component";
import { CheckinConfirmComponent } from "../../modals/checkin-confirm/checkin-confirm.component";
import { UpcomingBookingsComponent } from "../../modals/upcoming-bookings/upcoming-bookings.component";
import { WIDGET_LIST } from "../../../constants/quick-actions.constants";
import { NsWISConfigurations } from "../../../models/configurations.model";
import { NsEmpWebExpData } from "../../../models/emp-web-exp.model";
import { ApiService } from "../../../services/api.service";
import { BookingDataService } from "../../../services/booking-data.service";
import { ConfigurationService } from "../../../services/configuration.service";
import { HomeDataService } from "../../../services/home-data.service";
import { LoaderService } from "../../../services/loader.service";
import { DrawerService } from "mis-design-system/drawer";
import { ToastService } from "mis-crystal-design-system/toast";
import { EmpUserService } from "../../../services/emp-user.service";
import { parseZone, Moment } from "moment";
import { Subject, Observable, combineLatest, throwError } from "rxjs";
import { takeUntil, catchError, filter, switchMap, map, shareReplay, tap } from "rxjs/operators";
import { validateHeroBooking } from "../../../functions/booking-utils.functions";
import { filterOut, filterWidgetRefresh } from "../../../functions/custom-operators.functions";
import { WidgetService } from "../../../services/widget.service";
import { CONSTANTS_ASSETS_EMP_WEB_EXP } from "../../../constants/assets.constants";
import { FabItems } from "mis-design-system/fab";
import MISC from "../../../constants/misc.constants";
import { ModalService } from "mis-design-system/mis-modal";
import { BreakpointObserver } from "@angular/cdk/layout";
import { WidgetGroup } from "mis-crystal-design-system/widgets";
import { AsyncWidget } from "mis-crystal-design-system/widgets";
import { minLength } from "../../../functions/booking-utils.functions";
import { SyncWidget } from "mis-crystal-design-system/widgets";

const LayoutSettings = {
  columns: 2,
  breakpoints: {
    1250: {
      columns: 1
    }
  }
};

@Component({
  selector: "mis-employee-web-exp-body",
  templateUrl: "./body.component.html",
  styleUrls: ["./body.component.scss"]
})
export class BodyComponent implements OnInit, OnDestroy {
  timezone: string;
  Assets = CONSTANTS_ASSETS_EMP_WEB_EXP;
  fabItems: FabItems = [
    {
      label: "Work from Office",
      id: "WFO",
      icon: this.Assets.ICONS.IC_WFO,
      backgroundColor: "#0087B2",
      click: () => {
        this.handleQuickActions("OFFICE");
      }
    },
    {
      label: "Work from Home",
      id: "WFH",
      icon: this.Assets.ICONS.IC_WFH,
      backgroundColor: "#ED711C",
      click: () => {
        this.handleQuickActions("WFH");
      }
    }
  ];
  endObs = new Subject<void>();
  employeeData: NsEmpWebExpData.IEmployee;
  createAllowed: boolean;
  widgets = [];
  configurations: NsWISConfigurations.IConfigurations;
  cancelStream = this.bookingSvc.cancelBookings.pipe(takeUntil(this.endObs));

  teamCalendarUrl: string = "";
  displayTeamCalendar: boolean = false;
  upcomingBookingList: NsEmpWebExpData.IList[] = [];
  meetingBookingList: NsEmpWebExpData.IList[] = [];
  bookingSource: Observable<NsEmpWebExpData.IList[]>;
  meetingSource: Observable<NsEmpWebExpData.IList[]>;
  heroSource: Observable<NsEmpWebExpData.IBookingDetailDTOS>;
  @Output() showTeamCalendar = new EventEmitter(false);
  columnGroups: Observable<Array<Array<NsEmpWebExpData.IWidget>>>;
  isRedirected: boolean = false;
  bookingRequestStatusList: Set<string> = new Set(["CONFIRMED", "PENDING", "SIGNED_IN", "SIGNED_OUT"]);
  allWidgets: WidgetGroup;

  constructor(
    private homeData: HomeDataService,
    private user: EmpUserService,
    private toast: ToastService,
    private apiService: ApiService,
    public bookingSvc: BookingDataService,
    private drawerSvc: DrawerService,
    private modalSvc: ModalService,
    private loader: LoaderService,
    public configSvc: ConfigurationService,
    private router: Router,
    private widgetSvc: WidgetService,
    private breakpointObs: BreakpointObserver
  ) {}

  ngOnInit(): void {
    // this.modalSvc.show(IpConsentComponent, null);
    this.widgetSvc.refresh.pipe(takeUntil(this.endObs)).subscribe(group => {
      if (!group) {
        return this.allWidgets?.reset();
      } else if (group === "HERO") {
        return this.allWidgets.get("heroSource").reset();
      } else if (group === "UPCOMING") {
        return this.allWidgets.get("bookingSource").reset();
      } else if (group === "MEETINGS") {
        return this.allWidgets.get("meetingSource").reset();
      } else if (group === "BOOKINGS") {
        this.allWidgets.get("heroSource").reset();
        return this.allWidgets.get("bookingSource").reset();
      }
    });
    this.homeData.actionStream.pipe(takeUntil(this.endObs)).subscribe(res => {
      this.handleQuickActions(res);
    });
    this.getEmpDetails();
  }

  ngOnDestroy(): void {
    this.loader.hide();
    this.endObs.next();
    this.endObs.complete();
  }

  handlerCreateCallback = (id: "WFH" | "OFFICE") => {
    const configs = this.bookingSvc.setFormForCreate(this.configurations, this.employeeData, id);
    const ref = this.drawerSvc.show(BookingComponent, configs, { ...(this.configSvc.isMobile && { width: "100%" }) });
    ref.afterClosed.subscribe(res => {
      if (res === "DONE") {
        this.widgetSvc.refreshWidgets("BOOKINGS");
      }
    });
  };

  handleQuickActions(id: "OFFICE" | "WFH"): void {
    if (id === "OFFICE" || id === "WFH") {
      this.homeData.quickActionEvent(id, res => {
        if (res) this.handlerCreateCallback(id);
      });
    }
  }

  private getEmpDetails(): void {
    this.loader.show();
    combineLatest([
      this.apiService.getEmpTimezone().pipe(
        catchError(err => {
          this.loader.hide();
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to load timezone. ${JSON.stringify(err)}`);
        })
      ),
      this.configSvc.configurations.pipe(
        catchError(err => {
          this.loader.hide();
          this.toast.displayMsg(this.toast.messageBuilder.error.simple);
          return throwError(`Failed to load Configurations. ${JSON.stringify(err)}`);
        })
      ),
      this.homeData.employeeInfo
    ])
      .pipe(takeUntil(this.endObs))
      .subscribe(
        ([res, configs, employeeData]) => {
          this.loader.hide();
          this.timezone = res;
          this.bookingSvc.timezone = this.timezone;
          this.configurations = configs;
          this.employeeData = employeeData;
          const found = this.configurations?.BUSettings?.features.find(x => x.id === "booking");
          this.createAllowed = found?.createBookingEnabled || this.configurations.flattenedSettings.wfhBookingAllowed;
          this.homeData.configSubjectData(this.configurations);
          this.getBookings();
          this.updateWidgets();
          let date = new Date();
          date.setMonth(date.getMonth() + 1);
          document.cookie = "timezone=" + this.timezone + ";expires=" + date + ";domain=.moveinsync.com;path=/";
        },
        err => {
          this.toast.displayMsg("Failed to load required configurations.");
          this.loader.hide();
        }
      );
  }

  private updateWidgets(): void {
    this.widgets = WIDGET_LIST.filter(wdgt => wdgt.reqConfigs.every(cfg => this.configurations.flattenedSettings[cfg]));
    if (this.configSvc.isTeamsApp) {
      this.widgets = this.widgets.filter(wgt => wgt.type !== "calendar");
    }
    // layout testing
    // this.widgets.push(WIDGET_LIST[0], WIDGET_LIST[1], { ...WIDGET_LIST[2], id: 3 });
    this.updateLayout();
    // this.widgetSvc.refreshWidgets();
  }

  private updateLayout(): void {
    const breakpoints = Object.keys(LayoutSettings.breakpoints);
    this.columnGroups = this.breakpointObs.observe(breakpoints.map(bp => `(max-width: ${bp}px)`)).pipe(
      map(bpState => {
        let { columns } = LayoutSettings;
        if (bpState.matches) {
          columns = LayoutSettings.breakpoints[breakpoints.find(bp => bpState.breakpoints[`(max-width: ${bp}px)`])].columns;
        }
        const cols = this.widgets.reduce((acc, val, idx) => {
          const pos = idx % columns;
          if (acc[pos]) {
            acc[pos].push(val);
          } else {
            acc[pos] = [val];
          }
          return acc;
        }, []);
        // console.log(this.columnGroups);
        return cols;
      })
    );
  }

  private getBookings(): void {
    // this.loader.show();
    const start = parseZone().tz(this.timezone);
    this.allWidgets = new WidgetGroup({
      heroSource: new AsyncWidget(this.genHeroStream(start)),
      bookingSource: new AsyncWidget(this.genUpcomingBookingStream(start), [minLength(3)]),
      meetingSource: new AsyncWidget(this.genMeetingStream(start)),
      calendarSource: new SyncWidget()
    });
    // setTimeout(() => console.log(this.allWidgets.get("bookingSource").state), 5000);
  }

  private listenForBookingUpdates(type: "HERO_BOOKING" | "UPCOMING_BOOKING", data): void {
    if (this.isRedirected) return;
    if (type === "HERO_BOOKING") {
      data = data.getEmployee?.[0]?.bookingDetailDTOS;
    }
    if (!Array.isArray(data)) return;
    const getState = b => (type === "HERO_BOOKING" ? b : b.booking);
    let viewBooking = this.configSvc.viewBookingId && data.find(b => getState(b).bookingRequestId === this.configSvc.viewBookingId);
    viewBooking = viewBooking && typeof viewBooking === "object" && getState(viewBooking);
    let editBooking = this.configSvc.editBookingId && data.find(b => getState(b).bookingRequestId === this.configSvc.editBookingId);
    editBooking = editBooking && typeof editBooking === "object" && getState(editBooking);
    if (viewBooking || editBooking) {
      let type: "VIEW" | "EDIT";
      type = editBooking && viewBooking ? "VIEW" : (editBooking && "EDIT") || "VIEW";
      let bookingHandler;
      (async () => {
        if (type === "VIEW") {
          const bookingHandler = this.handleUpcoming.bind(this, viewBooking);
          return Promise.resolve(bookingHandler);
        } else if (type === "EDIT") {
          const bookingConfig = await this.configSvc.bookingConfigurations.toPromise();
          if (this.configSvc.validateBooking(editBooking, bookingConfig).editBookingAllowed) {
            const bookingHandler = this.handleUpdateBooking.bind(this, editBooking);
            return Promise.resolve(bookingHandler);
          }
        }
      })().then(bookingHandler => {
        if (bookingHandler) {
          bookingHandler.call(this);
        }
      });
      this.router.navigate([], {
        queryParams: {
          [MISC.VIEW_BOOKING_ID]: null,
          [MISC.EDIT_BOOKING_ID]: null
        },
        queryParamsHandling: "merge"
      });
      this.isRedirected = true;
    }
  }

  private genHeroStream(start: Moment): Observable<NsEmpWebExpData.IBookingDetailDTOS | undefined> {
    const end = start.clone().endOf("day");
    return this.apiService
      .getEmpProfile(this.apiService.genEmpDetailQuery(start, end, ["OFFICE", "WFH", "PARKING_TWO", "PARKING_FOUR", "MEALS"]))
      .pipe(
        tap(({ data }) => this.listenForBookingUpdates("HERO_BOOKING", data)),
        map(empDetail => {
          if (!empDetail?.dataPresent) {
            this.toast.displayMsg("Unable to load today's booking!");
            return;
          }
          const bookingsBase = this.bookingSvc.organiseBookings(empDetail?.data?.getEmployee?.[0]?.bookingDetailDTOS || []);
          const todaysBooking = bookingsBase?.filter(a => this.bookingRequestStatusList.has(a.booking.requestStatus));
          const booking = (todaysBooking?.length > 0 ? todaysBooking[0] : bookingsBase[0])?.booking;
          if (validateHeroBooking(booking, this.timezone)) {
            return booking;
          }
        })
      );
  }

  private genUpcomingBookingStream(start: Moment): Observable<NsEmpWebExpData.IList[]> {
    return this.bookingSvc.profileRules.pipe(
      switchMap(res => {
        const end = start.clone().add(res.bulkScheduleAllowedDays || this.bookingSvc.interval, "days");
        this.bookingSvc.setDates(start.valueOf(), end.valueOf());
        return this.apiService
          .getEmpProfile(this.apiService.genEmpDetailQuery(start.valueOf(), end.valueOf(), ["OFFICE", "WFH", "PARKING_TWO", "PARKING_FOUR", "MEALS"]))
          .pipe(
            map(res => {
              if (res.dataPresent) {
                return res?.data?.getEmployee?.[0]?.bookingDetailDTOS || [];
              } else {
                this.toast.displayMsg(res.errors?.[0]?.message || "Failed to load bookings!", 5000);
                return [];
              }
            })
          );
      }),
      map(res => {
        const bookingsBase = this.bookingSvc.organiseBookings(res || [], this.configurations.flattenedSettings.approvalFlowEnabled);
        const todaysBooking = bookingsBase?.filter(a => this.bookingRequestStatusList.has(a.booking.requestStatus));
        const firstBooking = todaysBooking?.length > 0 ? todaysBooking[0] : bookingsBase[0];
        const todayBookingRequestId =
          validateHeroBooking(firstBooking?.booking, firstBooking?.booking?.timezone) && firstBooking?.booking?.bookingRequestId;
        const bookings = bookingsBase?.filter(a => a.booking.bookingRequestId !== todayBookingRequestId);
        return bookings; //.slice(0, 2);
      }),
      tap(res => this.listenForBookingUpdates("UPCOMING_BOOKING", res)),
      filterOut("SIGNED_OUT")
    );
  }

  private genMeetingStream(start: Moment): Observable<NsEmpWebExpData.IList[]> {
    const end = start.clone().endOf("day");
    return this.apiService.getEmpProfile(this.apiService.genEmpDetailQuery(start.valueOf(), end.valueOf(), ["MEETING"], true)).pipe(
      map(res => {
        if (res.dataPresent) {
          return this.bookingSvc.organiseBookings(res.data?.getEmployee?.[0]?.bookingDetailDTOS || []);
        } else {
          this.toast.displayMsg(res.errors?.[0]?.message || "Failed to load meetings!", 5000);
          return [];
        }
      }),
      filterOut("SIGNED_OUT")
    );
  }

  setPreferences(): void {
    this.apiService.getEmpPreferences().subscribe(preferences => (this.bookingSvc.preferences = preferences));
  }

  handleUpcoming(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    this.bookingSvc.setFormForUpdate(this.configurations, this.employeeData, booking);
    const upcomingRef = this.drawerSvc.show(UpcomingBookingsComponent, { booking, configs: this.configurations });
    upcomingRef.afterClosed.subscribe(res => {
      if (res) {
        this.handleUpdateBooking(res);
      }
    });
  }

  handleMeetings(booking: NsEmpWebExpData.IBookingDetailDTOS) {
    this.router.navigate(["/" + this.user.environment.navLinks.meetingRooms], {
      queryParams: { viewMeetingId: booking.meetingId },
      queryParamsHandling: "merge"
    });
  }

  handleUpdateBooking(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    const config = this.bookingSvc.setFormForUpdate(this.configurations, this.employeeData, booking);
    const ref = this.drawerSvc.show(BookingComponent, config);
    ref.afterClosed.subscribe(res => {
      if (res === "DONE") {
        this.widgetSvc.refreshWidgets("BOOKINGS");
      }
    });
  }

  handleCardAction({ type, booking }: { type: NsEmpWebExpData.TCardActions; booking?: NsEmpWebExpData.IBookingDetailDTOS }): void {
    if (type === "Edit") {
      this.handleUpdateBooking(booking);
    } else if (type === "Cancel") {
      this.bookingSvc.setFormForUpdate(this.configurations, this.employeeData, booking);
      this.modalSvc.show(DeleteConfirmationComponent);
    } else if (type === "View") {
      this.handleUpcoming(booking);
    } else if (type === "Convert") {
      this.handleUpdateBooking({
        ...booking,
        bookingRequestType: booking.bookingRequestType === "OFFICE" ? "WFH" : "OFFICE"
      });
    }
  }

  handleCheckout(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    if (booking.bookingRequestType === "OFFICE") {
      this.officeCheckout(booking);
    } else if (booking.bookingRequestType === "WFH") {
      this.homeCheckout(booking);
    }
  }

  officeCheckout(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    this.loader.show();
    const now = parseZone().tz(this.timezone).valueOf();
    this.bookingSvc.offices
      .pipe(
        switchMap(offices => {
          const ofc = offices.find(o => o.guid === booking.officeId);
          return this.apiService.wfoUpdate({
            action: "SIGNED_OUT",
            bookingId: booking.bookingId,
            buId: this.user.employeeInfo.buid,
            empGuid: this.employeeData.empGuid,
            empId: this.employeeData.empId,
            isEntryAllowed: true,
            isScanSuccess: true,
            officeName: ofc?.address,
            requestId: booking.bookingRequestId,
            scanTime: String(now),
            scanType: "EMPLOYEE_WEB"
          });
        })
      )
      .subscribe(
        res => {
          this.loader.hide();
          if (res?.errorCode === 500) {
            this.toast.displayMsg(res.message);
            return;
          }
          this.toast.displayMsg("Checkout successful.");
          this.widgetSvc.refreshWidgets("HERO");
          this.widgetSvc.setWidgetAction("CHECK-OUT");
        },
        () => {
          this.loader.hide();
          this.toast.displayMsg("Something went wrong. Please try again after sometime.");
        }
      );
  }

  homeCheckout(booking: NsEmpWebExpData.IBookingDetailDTOS): void {
    this.loader.show();
    const now = parseZone().tz(this.timezone).valueOf();
    this.apiService
      .wfhUpdate(booking.bookingRequestId, {
        clockInTime: now,
        timestamp: now,
        type: "CLOCK_OUT"
      })
      .subscribe(res => {
        this.loader.hide();
        this.toast.displayMsg("Checkout successful.");
        this.widgetSvc.refreshWidgets("HERO");
        this.widgetSvc.setWidgetAction("CHECK-OUT");
      });
  }

  showCheckinConfirmPopup(bookingData: NsEmpWebExpData.IBookingDetailDTOS): void {
    const url = this.configurations.BUSettings.features.find(r => r.id === "communicationRegistry");
    const conf = this.configurations.BUSettings.features.find(r => r.id === "booking");
    const { IPBasedCheckinEnabled } = this.configurations.flattenedSettings;
    this.modalSvc.show(CheckinConfirmComponent, {
      config: { ...url, ...conf, IPBasedCheckinEnabled },
      booking: bookingData,
      employeeData: this.employeeData
    });
  }

  handleWidgetAction(widget: NsEmpWebExpData.IWidget, id: number) {
    if (widget?.id === 3 && id === 1) {
      this.router.navigate(["/" + this.user.environment.navLinks.meetingRooms], {
        queryParamsHandling: "preserve"
      });
    }
  }

  openTeamCalendar(val): void {
    this.showTeamCalendar.emit();
  }
}
